import { supabase } from "@/lib/supabase";
import { rooms as fallbackRooms } from "@/data/rooms";

const CACHE_KEY = "hpp-rooms-cache";
const AUTH_KEY = "hpp-admin-session";
const BUCKET = "room-images";

const toRoom = (row) => ({ ...row, amenityKeys: row.amenity_keys || [], updatedAt: row.updated_at, en: row.en || {} });
const toRow = (room) => ({
    slug: room.slug, name: room.name, tagline: room.tagline || "", capacity: room.capacity || "", beds: room.beds || "",
    photo: room.photo, description: room.description || "", amenity_keys: room.amenityKeys || [], gallery: room.gallery || [], en: room.en || {},
});
const throwIfError = (error) => { if (error) throw error; };

export async function getSession() {
    const { data, error } = await supabase.auth.getSession();
    throwIfError(error);
    return data.session;
}

export async function login(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    throwIfError(error);
    const { error: claimError } = await supabase.rpc("claim_first_admin");
    throwIfError(claimError);
    sessionStorage.setItem(AUTH_KEY, "1");
    return data.user;
}

export async function logout() {
    const { error } = await supabase.auth.signOut();
    throwIfError(error);
    sessionStorage.removeItem(AUTH_KEY);
}

export const getToken = () => sessionStorage.getItem(AUTH_KEY);

export async function loadRooms(forceRefresh = false) {
    if (!forceRefresh) {
        try {
            const cached = sessionStorage.getItem(CACHE_KEY);
            if (cached) return JSON.parse(cached);
        } catch { /* Caching is optional. */ }
    }
    const { data, error } = await supabase.from("rooms").select("*").order("name");
    throwIfError(error);
    const result = data.length ? data.map(toRoom) : fallbackRooms;
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(result)); } catch { /* Caching is optional. */ }
    return result;
}

export async function seedInitialRooms() {
    const { count, error: countError } = await supabase.from("rooms").select("id", { count: "exact", head: true });
    throwIfError(countError);
    if (count) return;
    const { error } = await supabase.from("rooms").insert(fallbackRooms.map(toRow));
    throwIfError(error);
    invalidateRoomsCache();
}

export const invalidateRoomsCache = () => sessionStorage.removeItem(CACHE_KEY);

export async function createRoom(room) {
    const { data, error } = await supabase.from("rooms").insert(toRow(room)).select().single();
    throwIfError(error);
    return toRoom(data);
}

export async function updateRoom(slug, room) {
    const { data, error } = await supabase.from("rooms").upsert(toRow(room), { onConflict: "slug" }).select().single();
    throwIfError(error);
    return toRoom(data);
}

export async function deleteRoom(slug) {
    const { error } = await supabase.from("rooms").delete().eq("slug", slug);
    throwIfError(error);
}

// User administration is handled by a protected Edge Function. The browser only
// sends the signed-in administrator's JWT; the privileged key never leaves Supabase.
const adminUsers = async (body) => {
    const { data, error } = await supabase.functions.invoke("admin-users", { body });
    if (error) {
        let message = "No se pudo administrar a los usuarios.";
        try {
            const response = await error.context?.json();
            message = response?.error || message;
        } catch { /* Use the safe fallback message. */ }
        throw new Error(message);
    }
    return data;
};

export const listUsers = async () => {
    const { data, error } = await supabase
        .from("profiles")
        .select("id, email, name")
        .eq("is_admin", true)
        .order("email");
    if (!error) return data || [];
    return (await adminUsers({ action: "list" })).users || [];
};
export const createUser = async ({ email, name, password }) => adminUsers({ action: "create", email, name, password });
export const updateUser = async ({ id, name, password }) => adminUsers({ action: "update", id, name, password });
export const deleteUser = async (id) => adminUsers({ action: "delete", id });
export const resetRooms = async () => { throw new Error("La restauración se administra mediante la migración de Supabase."); };

export async function uploadRoomPhoto(file) {
    if (!file.type.startsWith("image/")) throw new Error("El archivo debe ser una imagen.");
    if (file.size > 8 * 1024 * 1024) throw new Error("La imagen supera 8 MB.");
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `rooms/${crypto.randomUUID()}.${extension}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, { cacheControl: "31536000", upsert: false });
    throwIfError(error);
    return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
}

export function slugify(name, existing = []) {
    const base = name.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "habitacion";
    let slug = base;
    let i = 2;
    while (existing.includes(slug)) slug = `${base}-${i++}`;
    return slug;
}
